(() => {
  if (!customElements.get('science-native-carousel')) {
    class ScienceNativeCarousel extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.controller = new AbortController();
        const listen = (target, event, callback, options = {}) => target?.addEventListener(event, callback, { ...options, signal: this.controller.signal });
        this.viewport = this.querySelector('[data-carousel-viewport]');
        this.slides = [...this.querySelectorAll('[data-carousel-slide]')];
        this.previousButton = this.querySelector('[data-carousel-previous]');
        this.nextButton = this.querySelector('[data-carousel-next]');
        this.dots = this.querySelector('[data-carousel-dots]');
        this.index = 0;
        this.autoplayTimer = null;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (!this.viewport || this.slides.length === 0) return;
        this.centered = this.dataset.centered === 'true' && this.slides.length > 1;
        // 循环与居中分开控制，海报保留原来的三列左对齐布局。
        this.loopRequested = (this.centered || this.dataset.loop === 'true') && this.slides.length > 1;
        this.isMoving = false;
        // 首尾补足可见卡片：跨边界时先滑到副本，停稳后再无动画复位。
        if (this.loopRequested) {
          this.slides.forEach((slide, index) => { slide.dataset.carouselIndex = String(index); });
          const copySlide = (slide) => {
            const clone = slide.cloneNode(true);
            clone.dataset.carouselClone = 'true';
            clone.removeAttribute('data-carousel-slide');
            clone.removeAttribute('data-shopify-editor-block');
            clone.removeAttribute('id');
            clone.querySelectorAll('[id], [data-shopify-editor-block]').forEach((element) => {
              element.removeAttribute('id');
              element.removeAttribute('data-shopify-editor-block');
            });
            clone.setAttribute('aria-hidden', 'true');
            clone.inert = true;
            return clone;
          };
          const bufferCount = Math.min(this.slides.length, Math.max(2, Number(this.dataset.slidesDesktop) || 1, Number(this.dataset.slidesMobile) || 1));
          this.leadingSlides = this.slides.slice(-bufferCount).map(copySlide);
          this.trailingSlides = this.slides.slice(0, bufferCount).map(copySlide);
          this.slides[0].before(...this.leadingSlides);
          this.slides.at(-1).after(...this.trailingSlides);
        }
        this.visualSlides = [...this.querySelectorAll('[data-carousel-slide], [data-carousel-clone]')];
        listen(this.previousButton, 'click', () => this.move(-1));
        listen(this.nextButton, 'click', () => this.move(1));
        listen(this, 'mouseenter', () => this.stopAutoplay());
        listen(this, 'mouseleave', () => this.startAutoplay());
        listen(this, 'focusin', () => this.stopAutoplay());
        listen(this, 'focusout', (event) => { if (!this.contains(event.relatedTarget)) this.startAutoplay(); });
        listen(this, 'touchstart', () => this.stopAutoplay(), { passive: true });
        listen(this, 'touchend', () => this.startAutoplay(), { passive: true });
        listen(this.viewport, 'scroll', () => this.updateFromScroll(), { passive: true });
        listen(window, 'resize', () => this.render(), { passive: true });
        listen(this.reducedMotion, 'change', () => { if (this.loopRequested) this.render(); this.startAutoplay(); });
        listen(document, 'visibilitychange', () => this.startAutoplay());
        listen(this, 'shopify:block:select', (event) => {
          const index = this.slides.indexOf(event.target.closest('[data-carousel-slide]'));
          if (index >= 0) this.goTo(index);
          this.stopAutoplay();
        });
        this.buildDots();
        this.render();
        this.startAutoplay();
      }

      visibleCount() {
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        return Math.max(1, Number(mobile ? this.dataset.slidesMobile : this.dataset.slidesDesktop) || 1);
      }

      // 数量不足一屏时不循环；响应式切到单列后自动恢复。
      get looping() {
        return this.loopRequested && this.slides.length > (this.centered ? 1 : this.visibleCount());
      }

      maxIndex() {
        return Math.max(0, this.slides.length - (this.looping ? 1 : this.visibleCount()));
      }

      // 使用视口内部的布局坐标，不使用经过缩放的视觉坐标。
      slideOffset(slide) {
        if (!slide) return 0;
        // 居中坐标保留负值，避免少量卡片时多个左侧副本被截成 0 后误判为同一张。
        return this.centered
          ? slide.offsetLeft - (this.viewport.clientWidth - slide.offsetWidth) / 2
          : Math.max(0, slide.offsetLeft);
      }

      move(direction) {
        // 切换期间直接忽略额外点击，不排队；滚动停稳并完成首尾复位后解锁。
        if (this.looping && this.isMoving) return;
        const maximum = this.maxIndex();
        const previousIndex = this.index;
        this.index = direction > 0
          ? (this.index >= maximum ? 0 : this.index + 1)
          : (this.index <= 0 ? maximum : this.index - 1);
        let target = this.slides[this.index];
        if (this.looping && direction > 0 && previousIndex === maximum) target = this.trailingSlides[0];
        if (this.looping && direction < 0 && previousIndex === 0) target = this.leadingSlides.at(-1);
        this.render(true, target);
        this.restartAutoplay();
      }

      goTo(index) {
        this.index = Math.min(Math.max(0, index), this.maxIndex());
        this.render(true);
        this.restartAutoplay();
      }

      render(smooth = false, target = null) {
        this.dataset.carouselLoopActive = this.looping ? 'true' : 'false';
        this.index = Math.min(this.index, this.maxIndex());
        target ||= this.slides[this.index];
        this.isMoving = this.looping && smooth && !this.reducedMotion.matches;
        this.viewport.scrollTo({
          left: this.slideOffset(target),
          behavior: smooth && !this.reducedMotion.matches ? 'smooth' : (this.looping ? 'instant' : 'auto')
        });
        this.updateControls();
        // 同一位置或减少动态效果时可能不触发 scroll，仍需结束本次切换。
        if (this.looping) this.updateFromScroll();
      }

      updateFromScroll() {
        window.clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => {
          const left = this.viewport.scrollLeft;
          let closestIndex = 0;
          let closestDistance = Number.POSITIVE_INFINITY;
          const candidates = this.looping ? this.visualSlides : this.slides;
          candidates.forEach((slide, index) => {
            const distance = Math.abs(this.slideOffset(slide) - left);
            if (distance < closestDistance) {
              closestIndex = index;
              closestDistance = distance;
            }
          });
          const closestSlide = candidates[closestIndex];
          this.index = this.looping ? Number(closestSlide.dataset.carouselIndex) : Math.min(closestIndex, this.maxIndex());
          if (this.looping && closestSlide.dataset.carouselClone === 'true') {
            // 副本与原卡片的高亮状态同步，复位前后画面完全一致。
            this.viewport.scrollTo({ left: this.slideOffset(this.slides[this.index]), behavior: 'instant' });
          }
          this.isMoving = false;
          this.updateControls();
        }, 80);
      }

      buildDots() {
        if (!this.dots) return;
        this.dots.replaceChildren();
        this.slides.forEach((_, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.carouselDot = String(index);
          button.setAttribute('aria-label', `Go to slide ${index + 1}`);
          button.addEventListener('click', () => this.goTo(index));
          this.dots.append(button);
        });
      }

      updateControls() {
        const maximum = this.maxIndex();
        this.querySelectorAll('[data-carousel-dot]').forEach((dot, index) => {
          dot.hidden = index > maximum;
          dot.setAttribute('aria-current', index === this.index ? 'true' : 'false');
        });
        this.slides.forEach((slide, index) => {
          const visible = this.centered
            ? Math.abs(index - this.index) <= Math.floor(this.visibleCount() / 2)
            : index >= this.index && index < this.index + this.visibleCount();
          slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
          slide.inert = !visible;
          slide.dataset.carouselActive = index === this.index ? 'true' : 'false';
        });
        if (this.centered) {
          this.visualSlides.forEach((slide) => {
            slide.dataset.carouselActive = Number(slide.dataset.carouselIndex) === this.index ? 'true' : 'false';
          });
        }
      }

      restartAutoplay() {
        this.stopAutoplay();
        this.startAutoplay();
      }

      startAutoplay() {
        this.stopAutoplay();
        if (this.dataset.autoplay !== 'true' || this.reducedMotion.matches || document.hidden || this.maxIndex() === 0 || this.mediaPlaying || this.matches(':hover') || this.matches(':focus-within')) return;
        const delay = Math.max(3, Number(this.dataset.autoplaySeconds) || 5) * 1000;
        this.autoplayTimer = window.setInterval(() => this.move(1), delay);
      }

      stopAutoplay() {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }

      disconnectedCallback() {
        // 主题编辑器卸载 section 时清理监听与计时器，避免重新加载后重复轮播。
        this.stopAutoplay();
        window.clearTimeout(this.scrollTimer);
        this.isMoving = false;
        this.controller?.abort();
        this.controller = null;
        this.querySelectorAll('[data-carousel-clone]').forEach((clone) => clone.remove());
      }
    }
    customElements.define('science-native-carousel', ScienceNativeCarousel);
  }

  if (!customElements.get('science-research-grid')) {
    class ScienceResearchGrid extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        // 避免覆盖 HTMLElement 原生 title 字符串属性，保持弹窗标题为 DOM 节点。
        this.modalTitleElement = this.dialog?.querySelector('[data-modal-title]');
        this.body = this.dialog?.querySelector('[data-modal-body]');
        this.querySelectorAll('[data-research-trigger]').forEach((button) => {
          button.addEventListener('click', () => this.open(button));
        });
        this.querySelectorAll('[data-modal-close]').forEach((button) => button.addEventListener('click', () => this.close()));
        this.dialog?.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });
        this.dialog?.addEventListener('close', () => this.opener?.focus());
      }

      open(button) {
        if (!this.dialog || !this.modalTitleElement || !this.body) return;
        const source = this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
        this.modalTitleElement.textContent = button.dataset.title || '';
        this.body.innerHTML = source?.innerHTML || '';
        this.opener = button;
        this.dialog.showModal();
      }

      close() {
        this.dialog?.close();
      }
    }
    customElements.define('science-research-grid', ScienceResearchGrid);
  }

  if (!customElements.get('science-video-library')) {
    class ScienceVideoLibrary extends HTMLElement {
      connectedCallback() {
        this.querySelectorAll('[data-video-play]').forEach((button) => {
          button.addEventListener('click', () => this.play(button));
        });
      }

      play(button) {
        const carousel = button.closest('science-native-carousel');
        const youtubeId = button.dataset.youtubeId || '';
        // 仅允许标准视频 ID，错误设置不生成无效外部 iframe。
        if (!/^[\w-]{11}$/.test(youtubeId)) {
          console.warn('[Science video] 视频 ID 无效，已取消播放。');
          return;
        }
        const frame = document.createElement('iframe');
        frame.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(youtubeId)}?autoplay=1&rel=0`;
        frame.title = button.dataset.videoTitle || 'YouTube video';
        frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        frame.allowFullscreen = true;
        frame.loading = 'lazy';
        frame.className = 'science-video-library__iframe';
        button.closest('[data-video-frame]')?.replaceChildren(frame);
        if (carousel) {
          carousel.mediaPlaying = true;
          carousel.stopAutoplay();
        }
      }
    }
    customElements.define('science-video-library', ScienceVideoLibrary);
  }
})();
